import type { ImageInsight, ImageInsightType } from '../../services/galleryService';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { useTranslation } from '../../i18n/useTranslation';
import './CoralCheckFindings.css';

/** Health first — it is what the coral check is about — then algae, growth and species. */
const ORDER: readonly ImageInsightType[] = ['disease_detection', 'algae_detection', 'growth_analysis', 'species_identification'];

export interface CoralCheckFindingsProps {
  insights: readonly ImageInsight[];
}

/**
 * The AI findings of one photo, ordered for the coral check: the "Health & stress"
 * finding leads (first and larger), the others follow. Each shows its summary,
 * observations and confidence as text.
 */
export function CoralCheckFindings({ insights }: CoralCheckFindingsProps) {
  const { t } = useTranslation();
  const sorted = [...insights].sort((a, b) => ORDER.indexOf(a.analysisType) - ORDER.indexOf(b.analysisType));

  return (
    <ul className="coral-check-findings" data-testid="coral-check-findings">
      {sorted.map((insight) => {
        const lead = insight.analysisType === 'disease_detection';
        return (
          <li
            key={insight.id}
            className={`coral-check-findings__item${lead ? ' coral-check-findings__item--lead' : ''}`}
            data-testid={`coral-check-finding-${insight.analysisType}`}
          >
            <Heading level={3} className="coral-check-findings__title">
              {t(`gallery.details.ai.types.${insight.analysisType}`)}
            </Heading>
            {insight.summary && <Text variant={lead ? 'body' : 'body-sm'}>{insight.summary}</Text>}
            {insight.observations.length > 0 && (
              <ul className="coral-check-findings__observations">
                {insight.observations.map((o, i) => (
                  <li key={i}>
                    <Text as="span" variant="body-sm">{o}</Text>
                  </li>
                ))}
              </ul>
            )}
            {insight.confidence !== null && (
              <Text as="span" variant="caption">
                {t('gallery.details.ai.confidence', { percent: Math.round(insight.confidence * 100) })}
              </Text>
            )}
          </li>
        );
      })}
    </ul>
  );
}
